<template>
  <div class="page-card">
    <div class="page-header">
      <h2 class="page-title">艺人实名审核详情</h2>
      <el-button @click="goBack">返回列表</el-button>
    </div>

    <el-descriptions :column="2" border>
      <el-descriptions-item label="艺人姓名">{{ getBaseInfo('artistName') }}</el-descriptions-item>
      <el-descriptions-item label="昵称">{{ getBaseInfo('nickname') || '-' }}</el-descriptions-item>
      <el-descriptions-item label="手机号">{{ getBaseInfo('mobile') || '-' }}</el-descriptions-item>
      <el-descriptions-item label="实名单号">{{ getBaseInfo('authNo') || '-' }}</el-descriptions-item>
      <el-descriptions-item label="认证主体">{{ subjectTypeText(getBaseInfo('authSubjectType')) }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusText(getBaseInfo('status')) }}</el-descriptions-item>
      <el-descriptions-item label="证件姓名">{{ getBaseInfo('subjectName') || '-' }}</el-descriptions-item>
      <el-descriptions-item v-if="!showMinorGuardianInfo" label="监护人关系">{{ getBaseInfo('guardianRelation') || '-' }}</el-descriptions-item>
      <el-descriptions-item :span="showMinorGuardianInfo ? 1 : 2" label="证件号码">{{ getBaseInfo('subjectIdNo') || '-' }}</el-descriptions-item>
      <template v-if="showMinorGuardianInfo">
        <el-descriptions-item label="监护人手机号">{{ getBaseInfo('guardianMobile') || '-' }}</el-descriptions-item>
        <el-descriptions-item label="监护人证件号码">{{ getBaseInfo('guardianSubjectIdNo') || '-' }}</el-descriptions-item>
      </template>
    </el-descriptions>

    <el-divider>实名材料</el-divider>
    <div v-if="visibleMaterials.length" class="material-grid">
      <div v-for="(item, index) in visibleMaterials" :key="item.id || item.mediaUrl || index" class="material-item">
        <div class="material-label">{{ materialTypeText(item.mediaType) }}</div>
        <el-image
          :src="item.mediaUrl"
          :preview-src-list="previewImages"
          fit="cover"
          class="material-image"
        >
          <div slot="error" class="material-error">图片加载失败</div>
        </el-image>
      </div>
    </div>
    <el-empty v-else description="暂无实名材料" :image-size="88" />

    <el-divider>审核记录</el-divider>
    <el-table :data="detail.auditHistory || []" border>
      <el-table-column label="状态">
        <template slot-scope="{ row }">
          {{ statusText(row.status) }}
        </template>
      </el-table-column>
      <el-table-column prop="reason" label="原因" />
      <el-table-column prop="operatorName" label="处理人" />
      <el-table-column prop="operatedAt" label="处理时间" />
    </el-table>

    <div class="action-bar">
      <el-button type="success" @click="approve">审核通过</el-button>
      <el-button type="danger" @click="openRejectDialog">驳回</el-button>
    </div>

    <el-dialog title="驳回原因" :visible.sync="dialogVisible" width="480px" @closed="resetRejectDialog">
      <el-input v-model="reason" type="textarea" :rows="4" placeholder="请输入驳回理由" />
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="reject">确认</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import { approveArtistRealname, getArtistRealnameAuditDetail, rejectArtistRealname } from '../../api/audit'
import { navigateWithFallback } from '../../utils/admin-navigation'
import { normalizeMediaItem } from '../../utils/format'

const DEFAULT_STATUS = 1
const DEFAULT_PAGE = 1

export default {
  name: 'ArtistRealnameDetailPage',
  data() {
    return {
      detail: {},
      dialogVisible: false,
      reason: '',
      submitting: false
    }
  },
  computed: {
    showMinorGuardianInfo() {
      const value = this.getBaseInfo('minorSelfAuth')
      return value === true || value === 'true' || value === 1 || value === '1'
    },
    visibleMaterials() {
      return (this.detail.materials || []).map(normalizeMediaItem).filter(Boolean)
    },
    previewImages() {
      return this.visibleMaterials
        .map(item => item.mediaUrl)
        .filter(Boolean)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    getBaseInfo(key) {
      return this.detail.baseInfo ? this.detail.baseInfo[key] : ''
    },
    subjectTypeText(value) {
      if (value === 1 || value === '1') return '本人'
      if (value === 2 || value === '2') return '监护人'
      return value || '-'
    },
    materialTypeText(type) {
      if (type === 'idCardFrontUrl') return '身份证正面'
      if (type === 'idCardBackUrl') return '身份证反面'
      return type || '实名材料'
    },
    statusText(status) {
      if (status === 1) return '待审核'
      if (status === 2) return '通过'
      if (status === 3) return '驳回'
      return status == null ? '-' : String(status)
    },
    goBack() {
      const query = this.$route.query || {}
      if (query.from === 'dashboard') {
        navigateWithFallback(this, {
          path: '/dashboard',
          query: {
            auditType: query.fromType || 'artist-realname',
            auditPage: query.fromPage || String(DEFAULT_PAGE)
          }
        })
        return
      }
      navigateWithFallback(this, {
        path: '/audits',
        query: {
          type: query.fromType || 'artist-realname',
          status: query.fromStatus || String(DEFAULT_STATUS),
          page: query.fromPage || String(DEFAULT_PAGE)
        }
      })
    },
    async fetchData() {
      try {
        this.detail = await getArtistRealnameAuditDetail(this.$route.params.id)
      } catch (error) {
        this.$message.error(error.message || '加载艺人实名详情失败')
      }
    },
    async approve() {
      if (this.submitting) return
      let message = '确认审核通过该艺人实名吗？'
      if (this.showMinorGuardianInfo) {
        message = [
          '该艺人为未成年本人认证，请核对监护人信息：',
          `监护人手机号：${this.getBaseInfo('guardianMobile') || '-'}`,
          `监护人证件号码：${this.getBaseInfo('guardianSubjectIdNo') || '-'}`,
          '',
          '确认审核通过吗？'
        ].join('\n')
      }
      try {
        await MessageBox.confirm(message, '审核确认', {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      try {
        this.submitting = true
        await approveArtistRealname(this.$route.params.id, { remark: '审核通过' })
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '审核通过失败')
      } finally {
        this.submitting = false
      }
    },
    openRejectDialog() {
      this.dialogVisible = true
    },
    resetRejectDialog() {
      this.reason = ''
    },
    async reject() {
      if (this.submitting) return
      if (!this.reason) {
        this.$message.warning('请输入驳回理由')
        return
      }
      try {
        this.submitting = true
        await rejectArtistRealname(this.$route.params.id, { reason: this.reason, remark: this.reason })
        this.dialogVisible = false
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '驳回失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.material-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}

.material-label {
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
}

.material-image {
  width: 100%;
  height: 180px;
  border-radius: 6px;
  background: #f5f7fa;
}

.material-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  background: #f5f7fa;
}

.action-bar {
  margin-top: 20px;
}
</style>
